'use client';

import { useEffect, useRef, RefObject } from 'react';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Traps keyboard focus inside the referenced container.
 * - Closes on Escape
 * - Focuses the first focusable element on mount
 * - Wraps Tab / Shift+Tab within the container
 * - Restores focus to the previously active element on unmount (WCAG 2.4.3)
 *
 * Uses a ref for onClose so the effect only runs once on mount — callers
 * do NOT need to wrap onClose in useCallback.
 */
export function useFocusTrap(onClose: () => void): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  // Stable ref to onClose — updated on every render but the effect never re-runs
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Save the element that had focus before the trap activated (for WCAG 2.4.3 focus restoration)
    const previouslyFocused = document.activeElement as HTMLElement;

    // Focus the first focusable element inside the trap
    const focusable = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS));
    focusable[0]?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (e.key !== 'Tab') return;

      const current = Array.from(el!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS));
      if (current.length === 0) { e.preventDefault(); return; }

      const first = current[0];
      const last = current[current.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    el.addEventListener('keydown', handleKeyDown);
    // Cleanup: remove listener and restore focus to trigger element
    return () => {
      el.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  // Empty deps — runs once on mount only; onClose accessed via stable ref above
  }, []);

  return ref;
}
